import React, { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import { useProspectLogo } from 'src/common/hooks/use-prospect-logo.hook';

import LoadingSpinnerLight from 'src/common/components/loading-spinner/light';
import { Journey } from 'src/common/interfaces/journey.interface';
import { NeueJourneyButton } from '../neue-button/journey';
import { MiniModal } from '../mini-modal';

import { RecipientSearchInput, CompanyInfo } from './recipient-search-input';
import { JourneySearchInput } from './journey-search-input';
import { trackSegmentEvent } from 'src/utils/segment';
import classNames from 'classnames';
import {
  apiAIGenerateSection,
  apiJourneyBrainImage,
  apiAIGenerateOutline,
  apiAIGenerateImagePrompt,
  apiAIGenerateImagesStyle2,
  apiAISummarizeWebiste,
  apiAIJourneyFromContent,
  apiAIJourneyFromFile,
} from 'src/utils/journeyApi';
import { Block, ImageBlockContent } from '../../types';
import { generateBlockId } from '../../helpers/generate-block-id';
import { generateKeyBetween } from 'fractional-indexing';
import { createImageBlockLayout, createTextBlockLayout } from '../../layout-manager';
import { convertMarkdownToHTML } from '../../journey-brain/use-journey-brain.hook';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import { NeueLoadingSpinner } from '../loading/spinner';
import { Nullable } from 'src/types/nullable.type';
import { useCurrentOrganization } from 'src/store/organization';
import { AutomagicRegularIcon, CloseSmallIcon } from 'src/monet/icons';
import AutomagicBgImage from 'src/images/create-journey-modal-automagic-button-background.small.jpg';
import { Button } from 'src/common/button';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import nlp from 'compromise';
import { ContentEditableInput } from '../content-editable-input';
import sample from 'lodash/sample';
import uniq from 'lodash/uniq';
import { FileUploadForm } from '../file-upload-form';
import { uploadFileToS3 } from 'src/common/helpers/file-upload';

type CreateJourneyModalProps = {
  openRef: React.MutableRefObject<(() => void) | undefined>;
  closeRef: React.MutableRefObject<(() => void) | undefined>;
};

const ANYTHING_SAMPLES = [
  'the space exploration',
  'the history of the kit homes',
  'the beauty of anime',
  'the history of the Megaplex',
  'the history of the medieval art',
  'the camping trip in Yosemite park',
  'the woodworking',
  'the 90s music',
  'the history of speakeasy bars',
];

let prev = '';
function randomCycle(): string {
  const rand = sample(ANYTHING_SAMPLES);
  if (!rand) return '';

  if (prev === rand) return randomCycle();
  prev = rand;
  return rand;
}

const examplePrompts = [
  {
    name: 'Create presentation about anything',
    prompt: (params: any) => `Create a presentation about ${randomCycle()}`,
  },
  {
    name: 'Pitch a product',
    prompt: (params: any) =>
      `You are sales professional at ${params.userOrganization}. Create a pitch for your product in less then 6 slides`,
  },
  {
    name: 'Pitch a product to another company',
    prompt: (params: any) =>
      `You are sales professional at ${params.userOrganization}. Create a pitch why sales team at grammarly.com should use your product`,
  },
  {
    name: 'Create a pitch deck template',
    prompt: (params: any) => `Create a pitch deck template`,
  },
  // {
  //   name: 'Create an onboarding plan',
  //   prompt: (params: any) => `You are customer success professional at ${params.userOrganization}. Create a customer onboarding plan for example.com`
  // },
  // {
  //   name: 'Create a mutual action plan',
  //   prompt: (params: any) => `You are sales professional at ${params.userOrganization}. Create a custom mutual action plan with a set of tasks, due dates and outcomes to help get a deal closed with example.com`
  // },
];

function extractText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent || '';
}

export const ToggleButton: React.FC<{
  selected: boolean;
  label: string | JSX.Element;
  type?: 'submit' | 'reset' | 'button' | undefined;
  onClick: () => void;
  disabled?: boolean;
  automagicBg?: boolean;
}> = ({ selected, type, label, onClick, disabled = false, automagicBg = false }) => {
  return (
    <button
      type={type}
      disabled={disabled}
      style={{ '--image-url': `url(${AutomagicBgImage})` } as React.CSSProperties}
      className={classNames('flex gap-2 flex-1 grow justify-center items-center rounded-lg transition', {
        'border-2 border-neue-journey-accent bg-neue-journey-accent-10': selected,
        'border-2 border-transparent bg-neue-journey-fg-10': !selected,
        'bg-[image:var(--image-url)] bg-center bg-[size:100%]': selected && automagicBg,
      })}
      onClick={(e) => {
        !disabled && onClick();
      }}
    >
      <div
        className={classNames('text-neue-journey-small grow text-left py-[8px] px-[12px]', {
          'bg-[#0080922B]': automagicBg && selected,
        })}
      >
        {label}
      </div>
    </button>
  );
};

export async function runWithRetries<T>(fn: () => Promise<T>, retries = 3): Promise<T | undefined> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (e: any) {
      if (e.code === 'RequestAbortedError') {
        return;
      }
      if (e.code === 'InvalidRequestError') {
        return Promise.reject(e);
      }
      if (i === retries - 1) {
        return Promise.reject({ code: 'ServerOverloaded' });
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }
}

async function genSlideText(topic: string, summaries: string[], slideInfo: any) {
  if (slideInfo.text_prompt && slideInfo.text_prompt.length > 0) {
    const slideResult = await runWithRetries(() =>
      apiAIGenerateSection({
        topic,
        prompt: slideInfo.text_prompt,
        title: slideInfo.title,
        urls_summaries: summaries,
      })
    );
    return slideResult.slide;
  } else {
    return null;
  }
}

async function analyzeUrls(prompt: string, progressFn: (progress: string) => void) {
  const doc = nlp(prompt);
  progressFn('Analyzing URLs');
  const summaries = Promise.all(
    uniq<string>(doc.urls().out('array')).map(async (url: string) => {
      let summary;
      try {
        const res = await apiAISummarizeWebiste({ url: url });
        summary = res.summary;
      } catch (e) {
        console.error(e);
        summary = null;
      }

      return {
        url,
        summary,
      };
    })
  );
  return (await summaries).filter((item) => item.summary !== null);
}

async function genSlideImages(topic: string, slideInfo: any, imgStyle: string) {
  if (slideInfo.image_prompt && slideInfo.image_prompt.length > 0) {
    const response = await runWithRetries(() =>
      apiJourneyBrainImage('generate_from_prompt', {
        prompt: slideInfo.image_prompt,
        count: 1,
        service: 'dalle',
        style_preset: imgStyle,
      })
    );
    return response.images;
  } else {
    return null;
  }
}

async function genImagePrompt(topic: string, slideInfo: any, text: string) {
  const response = await runWithRetries(() =>
    apiAIGenerateImagePrompt({
      slide_text: text,
      slide_title: slideInfo.title,
    })
  );
  return response['image_prompt'];
}

async function generatePromptExp(
  i: string,
  summaries: any,
  progressFn: (s: string) => void,
  setError: (s: string) => void
) {
  progressFn('Thinking about image styles');
  const tm1 = setTimeout(() => {
    progressFn('Starting to write the content');
  }, 15000);

  let slidesInfo, imgStyle: any, outline: any;
  try {
    [outline, imgStyle] = await Promise.all([
      runWithRetries(() => apiAIGenerateOutline({ topic: i, summaries })),
      runWithRetries(() =>
        apiAIGenerateImagesStyle2({ topic: i }).then((r) => {
          progressFn('Deciding on the Journey title');
          return r;
        })
      ),
    ]);
    clearTimeout(tm1);

    progressFn('Thinking of some great images to use');
    let slidesGenerated = 0;

    slidesInfo = await Promise.all(
      outline.slides.map(async (slideInfo: any) => {
        try {
          const text = await genSlideText(i, summaries, slideInfo);
          const imagePrompt = await genImagePrompt(i, slideInfo, text);
          const images = await genSlideImages(i, { ...slideInfo, image_prompt: imagePrompt }, imgStyle.style);
          slidesGenerated++;
          progressFn(`Generated ${slidesGenerated} out of ${outline.slides.length} sections`);

          const enriched = { ...slideInfo };
          if (imagePrompt) {
            enriched.image_prompt = imagePrompt;
          }
          if (text) {
            enriched.text = text;
          }
          if (images) {
            enriched.images = images;
          }

          return enriched;
        } catch (e) {
          console.log(e);
          return slideInfo;
        }
      })
    );
  } catch (e: any) {
    if (e.code === 'InvalidRequestError') {
      console.error(e);
      setError('Sorry, something went wrong. We are looking into it');
      return;
    } else if (e.code === 'ServerOverloaded') {
      console.error(e);
      setError('Sorry, our server is overloaded. Please, try again later');
      return;
    } else {
      console.error(e);
      setError('Sorry, something went wrong. We are looking into it');
      return;
    }
  }

  progressFn(`Wrapping it up for you`);

  const generatedSections: [{ id: string; title: string; blocks: Block[] }] = [
    {
      id: generateBlockId(),
      title: slidesInfo[0].title,
      blocks: [
        {
          id: generateBlockId(),
          content: {
            type: 'text',
            prompt: '',
            value: `<h1 style="text-align:center">${slidesInfo[0].title}</h1><h2 style="text-align:center">${slidesInfo[0].subtitle}</h2>`,
          },
          layout: createTextBlockLayout('100%'),
          position: generateKeyBetween(null, null),
        },
      ],
    },
  ];

  for (const slideInfo of slidesInfo.slice(1)) {
    const title = convertMarkdownToHTML(slideInfo.title);
    const blocks: Block[] = [];
    blocks.push({
      id: generateBlockId(),
      content: {
        type: 'text',
        prompt: '',
        value: `<h2 style="text-align:center">${title}</h2>`,
      },
      layout: createTextBlockLayout('100%'),
      position: '',
    });

    const hasText = slideInfo.text_prompt && slideInfo.text_prompt.length > 0;
    const hasImage = slideInfo.image_prompt && slideInfo.image_prompt.length > 0;

    let textBlock: Nullable<Block> = null;
    if (hasText) {
      textBlock = {
        id: generateBlockId(),
        content: {
          type: 'text',
          horizontalAlignment: 'left',
          prompt: slideInfo.text_prompt,
          value: convertMarkdownToHTML(slideInfo.text).replaceAll('>\n<', '><'),
        },
        layout: createTextBlockLayout('100%'),
        position: '',
      };
    }

    let imageBlock: Nullable<Block> = null;
    if (hasImage) {
      imageBlock = {
        id: generateBlockId(),
        content: {
          type: 'image',
          url: slideInfo.images[0].url,
          objectFit: 'contain',
          dalleParams: {
            prompt: slideInfo.image_prompt,
            urls: slideInfo.images.map((image: any) => image.url),
          },
        },
        layout: createImageBlockLayout('100%', { aspectRatio: null }),
        position: '',
      };
    }

    if (textBlock && imageBlock) {
      if (slideInfo.text.length > 830) {
        imageBlock.layout.spec.width = '100%';
        blocks.push(imageBlock);
        textBlock.layout.spec.width = '100%';
        blocks.push(textBlock);
      } else {
        textBlock.layout.spec.width = '50%';
        blocks.push(textBlock);
        imageBlock.layout.spec.width = '50%';
        (imageBlock.content as ImageBlockContent).objectFit = 'cover';
        blocks.push(imageBlock);
      }
    } else if (textBlock) {
      textBlock.layout.spec.width = '100%';
      blocks.push(textBlock);
    } else if (imageBlock) {
      imageBlock.layout.spec.width = '100%';
      blocks.push(imageBlock);
    }

    let lastPosition = null;
    for (const block of blocks) {
      lastPosition = generateKeyBetween(lastPosition, null);
      block.position = lastPosition;
    }

    generatedSections.push({
      title: slideInfo.title,
      id: generateBlockId(),
      blocks: blocks,
    });
  }

  return { sections: generatedSections, imageStyle: imgStyle.style };
}

type ActiveTabEnum = 'new' | 'automagic' | 'existing';

export const CreateJourneyModal = ({ openRef, closeRef }: CreateJourneyModalProps) => {
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const [selectedRecipient, setSelectedRecipient] = useState<CompanyInfo | null>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const sellerDomain = (currentOrganization.url || '').replaceAll('https://', '').replaceAll('www.', '');
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTabEnum>('automagic');
  const [aiTopic, setAiTopic] = useState('');
  const initialRef = useRef(null);

  const [loadingStage, setLoadingStage] = useState<string | null>();
  const [generationError, setGenerationError] = useState<string | null>();
  const { anchorRef, anchorStyle, renderSpotlight } = useSpotlight({
    placement: 'bottom',
    offset: 24,
    styling: 'neue-tooltip',
    theme: 'journey',
  });

  const [showExamples, setShowExamples] = useState(false);
  const examplesSpotlight = useSpotlight({ placement: 'right', offset: 24, styling: 'neue-tooltip', theme: 'journey' });

  const history = useHistory();

  const { createNeueJourney } = useNewJourneyStore((state) => ({
    createNeueJourney: state.createNeueJourney,
  }));

  const api = useSpringRef();
  const [showExamplesStyles] = useSpring(
    () => ({
      ref: api,
      from: { y: 8, opacity: 0 },
      to: { y: 0, opacity: 1 },
    }),
    []
  );

  useEffect(() => {
    if (activeTab !== 'automagic') {
      setShowExamples(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (showExamples) {
      api.start({
        from: { y: 8, opacity: 0 },
        to: { y: 0, opacity: 1 },
      });
    } else {
      api.start({ opacity: 0 });
    }
  }, [showExamples]);

  const {
    fetchProspectLogo,
    status,
    fetching,
    logoUrl,
    setLogoUrl,
    generatedSlug,
    setGeneratedSlug,
    domain,
    setDomain,
  } = useProspectLogo();

  const onDialogClose = () => {
    setSelectedJourney(null);
    setSelectedRecipient(null);
    setRecipientSearchQuery('');
    setLogoUrl(null);
    setDomain(null);
    setGeneratedSlug(null);
    setShowExamples(false);
    setAiTopic('');
    setGenerationError(null);
  };

  const onSelectRecipient = async (recipient: CompanyInfo) => {
    setSelectedRecipient(recipient);
    if (recipient) {
      fetchProspectLogo(recipient.domain);
    }
  };

  const onResetRecipient = () => {
    setLogoUrl(null);
    setDomain(null);
    setGeneratedSlug(null);
  };

  const getJourneyName = (selectedJourney: Journey | null, recipientName: string) => {
    if (selectedJourney) {
      if (recipientName) {
        return `${selectedJourney.name} for ${recipientName}`;
      } else {
        return selectedJourney.name;
      }
    }

    return 'Untitled Journey';
  };

  const onClickCreate = async ({ input, file }: { input?: string; file?: File }) => {
    try {
      setGenerationError(null);
      setShowExamples(false);
      setCreating(true);
      const recipientName = selectedRecipient ? selectedRecipient.name : recipientSearchQuery || '';

      trackSegmentEvent('Create Journey Modal Action Clicked', {
        action: 'start_your_journey',
        for: recipientName,
        with_duplicate_journey: !!selectedJourney,
        type: activeTab,
        topic: input,
      });

      let data;
      let precreatedSections = null;
      let name = getJourneyName(selectedJourney, recipientName);
      if (activeTab === 'new') {
        // onClickSkip();
      } else if (activeTab === 'automagic') {
        if (input || file) {
          // const summaries = await analyzeUrls(aiTopic, setLoadingStage);
          let genRes;

          if (input) {
            genRes = await apiAIJourneyFromContent({ content: input });
          } else if (file) {
            const response = await uploadFileToS3(file, 'create-from-content-input-files');
            const request = { file_url: response.url };
            genRes = await apiAIJourneyFromFile(request);
          } else {
            return;
          }

          if (!genRes) {
            setCreating(false);
            return;
          }

          precreatedSections = genRes.sections;
          name = genRes.journey_title;
          data = {};
        }
      }

      createNeueJourney({
        history,
        name,
        generatedSlug: generatedSlug,
        sellerDomain: sellerDomain,
        recipientDomain: recipientSearchQuery,
        journeyToDuplicate: selectedJourney,
        recipientLogoUrl: logoUrl,
        recipientName: recipientName,
        precreatedSections: precreatedSections,
        data,
      });
    } catch (e) {
      setCreating(false);
      console.error(e);
    }
  };

  const renderIconWithLoader = (fetching: boolean, url?: Nullable<string>) => {
    if (fetching) {
      return (
        <div className='bg-neue-journey-fg-10 rounded-lg p-2 w-10 h-10 flex-shrink-0 flex items-center justify-center'>
          <LoadingSpinnerLight />
        </div>
      );
    }

    if (url) {
      return (
        <div className='bg-neue-journey-fg-10 rounded-lg p-2 flex-shrink-0 max-w-[100px] min-w-[40px]'>
          <img src={url} className='h-6 w-auto' />
        </div>
      );
    }

    return null;
  };

  let panelContent;
  if (activeTab === 'new') {
    panelContent = (
      <>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col'>
            <div className='text-bedrock-p-strong'>Who is this for</div>
            <div className='text-bedrock-p-small mt-1'>Automagically generate a custom Journey for this recipient</div>
            <div className='mt-4 flex items-center justify-between space-x-2'>
              <RecipientSearchInput
                inputRef={initialRef}
                autoFocus={true}
                placeholder="Enter recipient's website URL (optional)"
                onResetRecipient={onResetRecipient}
                onSelectRecipient={onSelectRecipient}
                onRecipientSearchQueryChange={(value: string) => setRecipientSearchQuery(value)}
                hasSelectedRecipient={!!selectedRecipient}
              />
              {renderIconWithLoader(fetching, logoUrl)}
            </div>
          </div>
        </div>
      </>
    );
  } else if (activeTab === 'automagic') {
    panelContent = (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-col'>
            <div className='text-bedrock-p'>
              Describe your Journey and have a draft automagically written for you. Use domains, like salesforce.com,
              when referencing companies.
            </div>
          </div>
          <div className='mt-4 mb-px'>
            <div className='flex items-center space-x-2'>
              <div className='flex flex-1 flex-col'>
                <div className='mb-px relative min-h-[124px]'>
                  <textarea
                    autoFocus
                    className='neue-journey-text-input min-h-[124px] pb-[50px] light w-full'
                    placeholder='Create presentation about anything'
                    value={aiTopic}
                    disabled={creating}
                    onChange={(e) => setAiTopic(e.target.value)}
                    onKeyDown={(e) => !e.shiftKey && e.key === 'Enter' && onClickCreate({ input: aiTopic })}
                  ></textarea>
                  <FileUploadForm
                    onNewFile={(file: File) => {
                      onClickCreate({ file });
                    }}
                    accept={undefined}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  } else {
    panelContent = (
      <>
        <div className='flex flex-col'>
          <div className='text-neue-journey-medium-strong'>Duplicate a Journey</div>
          <div className='text-neue-journey-small mt-1'>Re-use the content of another Journey</div>
          <div className='mt-4'>
            <JourneySearchInput autoFocus={true} onSelectJourney={setSelectedJourney} />
          </div>
        </div>
        <div className='flex flex-col'>
          <div className='text-bedrock-p-strong'>Who is this for</div>
          <div className='text-bedrock-p-small mt-1'>Automagically insert the correct organization logos</div>
          <div className='flex items-center justify-between space-x-2 mt-4'>
            <RecipientSearchInput
              autoFocus={false}
              placeholder="Enter recipient's website URL (optional)"
              onResetRecipient={onResetRecipient}
              onSelectRecipient={onSelectRecipient}
              onRecipientSearchQueryChange={(value: string) => setRecipientSearchQuery(value)}
              hasSelectedRecipient={!!selectedRecipient}
            />
            {renderIconWithLoader(fetching, logoUrl)}
          </div>
        </div>
      </>
    );
  }

  let buttonLabel = 'Start Your Journey';
  if (activeTab === 'existing') {
    buttonLabel = 'Restart Your Journey';
  }

  const renderAutomagicLabel = () => {
    return (
      <div className='flex items-center space-x-2'>
        <AutomagicRegularIcon className='w-4 h-4 ' />
        <div>Automagic</div>
      </div>
    );
  };

  return (
    <MiniModal
      title='Create Journey'
      subtitle={
        <div className='flex flex-row gap-2'>
          <ToggleButton
            automagicBg
            disabled={creating}
            selected={activeTab === 'automagic'}
            label={renderAutomagicLabel()}
            onClick={() => setActiveTab('automagic')}
          />
          <ToggleButton
            disabled={creating}
            selected={activeTab === 'new'}
            label='New Journey'
            onClick={() => setActiveTab('new')}
          />
          <ToggleButton
            disabled={creating}
            selected={activeTab === 'existing'}
            label='Existing Journey'
            onClick={() => setActiveTab('existing')}
          />
        </div>
      }
      openRef={openRef}
      closeRef={closeRef}
      onClose={onDialogClose}
      initialFocus={initialRef}
    >
      <form
        onSubmit={(e) => {
          onClickCreate({ input: aiTopic });
          e.preventDefault();
        }}
        ref={examplesSpotlight.anchorRef}
        style={examplesSpotlight.anchorStyle}
      >
        <div className='flex flex-col space-y-6' ref={anchorRef} style={anchorStyle}>
          {panelContent}
          <div className='flex items-center space-x-2'>
            <div className='flex-1'>
              <NeueJourneyButton
                type='button'
                className='w-full h-10'
                onClick={() => onClickCreate({ input: aiTopic })}
                disabled={creating}
                tabIndex={0}
              >
                {creating ? <NeueLoadingSpinner theme='journey' size='16' /> : buttonLabel}
              </NeueJourneyButton>
            </div>
          </div>
        </div>
      </form>
      {renderSpotlight({
        content: generationError || loadingStage || '',
        show: (!!loadingStage || !!generationError) && activeTab === 'automagic',
        containerClassName: 'transition-all transition-300',
      })}
      <div
        className={classNames('absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full', {
          'pointer-events-none': !showExamples,
          'pointer-events-auto': showExamples,
        })}
      >
        <animated.div className='bg-black rounded-lg' style={showExamplesStyles}>
          <ExamplesSpotlight
            examplePrompts={examplePrompts}
            onExampleClick={(examplePrompt) =>
              setAiTopic(
                examplePrompt.prompt({
                  userOrganization: currentOrganization.url.replace(/http(s)?:\/\/(www\.)?/i, ''),
                })
              )
            }
            onCloseClick={() => setShowExamples(false)}
          />
        </animated.div>
      </div>
    </MiniModal>
  );
};

type ExamplePrompt = {
  name: string;
  prompt: (params: any) => string;
};
type ExamplesSpotlightProps = {
  examplePrompts: ExamplePrompt[];
  onExampleClick: (examplePrompt: ExamplePrompt) => void;
  onCloseClick: () => void;
};

const ExamplesSpotlight = (props: ExamplesSpotlightProps) => {
  return (
    <div className='flex flex-col p-4 rounded-lg gap-2'>
      <div className='text-bedrock-p relative'>
        <div className='font-medium'>Examples</div>
        <div className='absolute top-1/2 right-0 -translate-y-1/2'>
          <a href='javascript:void(0)' onClick={() => props.onCloseClick()}>
            <CloseSmallIcon />
          </a>
        </div>
      </div>
      {props.examplePrompts.map((examplePrompt, index) => {
        return (
          <a
            href='javascript:void(0)'
            onClick={() => props.onExampleClick(examplePrompt)}
            key={index}
            className='text-bedrock-p-small'
          >
            {examplePrompt.name}
          </a>
        );
      })}
    </div>
  );
};
